import { extendTheme } from '@chakra-ui/react';
import '@fontsource/work-sans';
import '@fontsource/inter';
import '@fontsource/work-sans/600.css';
import '@fontsource/work-sans/700.css';
import '@fontsource/open-sans';
import '@fontsource/open-sans/700.css';
import '@fontsource/roboto';
import '@fontsource/roboto/700.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

const theme = extendTheme({
  fonts: {
    heading: 'Work Sans',
    body: 'Work Sans',
    mono: 'Work Sans',
  },
  colors: {
    // Green theme (current). The yellow/gray brand palette (Pantone 109
    // FFD100 / Pantone 425 53575A) is used on the login page only for now;
    // to rebrand the whole app later, swap these scales — components reference
    // tokens, not hexes.
    primary: {
      100: '#075015',
      200: '#075015',
      300: '#107224',
      400: '#1E9435',
      500: '#2FB64A',
      600: '#44D862',
      700: '#5DFA7D',
      800: '#83FF9C',
      900: '#83FF9C50',
    },
    support: {
      100: '#304338',
      200: '#445C4E',
      300: '#597665',
      400: '#6F8F7D',
      500: '#304338',
      600: '#87A895',
      700: '#9FC2AE',
      800: '#B8DCC7',
    },
    // Pale backgrounds (table headers, row tints, control fills).
    surface: {
      100: '#F8FDFA',
      200: '#EEF6F1',
      300: '#E1EFE3',
      400: '#D8EBDF',
    },
    warning: {
      100: '#4F3A03',
      200: '#71540A',
      300: '#936F14',
      400: '#B58B22',
      500: '#F9C746',
      600: '#FFD771',
      700: '#FFE39A',
      800: '#FFEEC3',
    },
  },
  fontSizes: {
    xs: '12px',
    sm: '13px',
    md: '14px',
    lg: '18px',
    xl: '24px',
    '2xl': '26px',
    '3xl': '28px',
  },
  lineHeights: {
    normal: 'normal',
    none: '1',
    shorter: '1.25',
    short: '1.375',
    base: '1.5',
    tall: '1.625',
    taller: '2',
  },
  letterSpacings: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
  components: {
    Heading: {
      baseStyle: {
        fontWeight: 700,
        fontFamily: 'Poppins',
        color: 'support.500',
      },
    },
    FormLabel: {
      baseStyle: {
        fontWeight: 700,
        fontFamily: 'Open Sans',
        textTransform: 'uppercase',
        fontSize: 14,
        color: '#C7C7C7',
      },
    },
    Button: {
      baseStyle: {
        fontWeight: 'bold',
        backgroundColor: 'primary.500',
        borderRadius: '8px',
        letterSpacing: 'widest',
        _focus: {
          boxShadow: 'none',
        },
      },
      sizes: {
        xl: {
          h: '56px',
          fontSize: 'lg',
          px: '32px',
        },
      },
      variants: {
        'with-shadow': {
          bg: 'red.400',
        },
        // Sortable table headers use this. It was never registered, so those
        // buttons silently fell back to the default Button — including a
        // 2.5rem min-width and centred text, which indented short labels
        // ("Site", "Invoice") away from their column's left edge and out of
        // line with the values beneath them.
        plain: () => ({
          minWidth: 0,
          height: 'auto',
          padding: 0,
          background: 'transparent',
          fontWeight: 'inherit',
          justifyContent: 'flex-start',
          _hover: { background: 'transparent' },
          _active: { background: 'transparent' },
        }),
        solid: () => ({
          fontWeight: '600',
          bg: 'primary.500',
          color: '#fff',
          px: '20px',
          _hover: {
            backgroundColor: 'primary.300',
          },
          _active: {
            backgroundColor: 'primary.200',
          },
        }),
        outline: () => ({
          fontWeight: '600',
          px: '20px',
          borderColor: 'primary.500',
          backgroundColor: '#fff',
          color: 'primary.500',
          _hover: {
            backgroundColor: 'primary.800',
          },
          _active: {
            backgroundColor: 'primary.700',
          },
        }),
        ghost: () => ({
          fontWeight: '600',
          px: '20px',
          color: 'primary.500',
          bg: 'transparent',
          _hover: {
            color: 'primary.800',
          },
          _active: {
            color: 'primary.700',
          },
        }),
        danger: () => ({
          fontWeight: '600',
          bg: 'red.500',
          color: '#fff',
          px: '20px',
          _hover: {
            backgroundColor: 'red.600',
          },
          _active: {
            backgroundColor: 'red.700',
          },
        }),
      },
    },
    Tabs: {
      baseStyle: {
        tab: {
          _focus: {
            outline: 'none',
            boxShadow: 'none',
          },
        },
      },
      variants: {
        'enclosed-colored': () => ({
          tab: {
            border: 'primary.500',
            borderColor: 'primary.500',
          },
          tabpanel: {
            bg: '#FFFFFF 0% 0% no-repeat padding-box',
            boxShadow: '0px 3px 6px #00000029',
            borderRadius: '0px 0px 8px 8px',
            p: '20px',
          },
        }),
        line: (_props: any) => ({
          ..._props,
          tab: {
            ..._props?.tab,
            _selected: {
              ..._props?.tab?._selected,
              color: 'primary.200',
              opacity: 1,
              fontWeight: '600',
            },
          },
        }),
      },
    },
  },
});

export const scrollbarStyles = {
  scrollbarGutter: 'stable',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
    borderRadius: '24px',
  },
  '&::-webkit-scrollbar-track': {
    width: '10px',
    height: '10px',
    background: '#10722450',
    borderRadius: '24px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#1E9435',
    borderRadius: '24px',
  },
};

export default theme;
